/**
 * Learning Path Statistics Page
 * Shows both legacy interaction counts and phase-1 behavior analytics for heat, flow, and attention.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FireOutlined,
  LineChartOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import { QRCodeSVG } from 'qrcode.react';
import PagesWrapper from '../../PagesWrapper';
import {
  getLearningPath,
  getLearningPathBehaviorStatistics,
  getLearningPathFunnelStatistics,
  getLearningPathStatistics,
} from '../../services/learningPathsService';
import { getFileUrl } from '../../utils/urlUtils';
import { formatDurationFromMilliseconds } from '../../utils/timeUtils';
import styles from './LearningPaths.module.css';

const funnelTemplateFallbackLabels = {
  'learning-intent': 'Learning intent',
  'resource-engagement': 'Resource engagement',
  'profile-journey': 'Training profile journey',
};

const funnelTemplateFallbackDescriptions = {
  'learning-intent': 'How many visits progress from opening the path to opening a hotspot, then a video, then a quiz, and finally a survey.',
  'resource-engagement': 'How many visits move from opening a hotspot to opening any resource, then continue into quiz and survey steps.',
  'profile-journey': 'How many visits progress from opening the path to opening a hotspot and then reaching a training profile.',
};

const funnelStageFallbackLabels = {
  visit: 'Visit',
  'hotspot-open': 'Element open',
  'video-open': 'Video open',
  'quiz-open': 'Quiz open',
  'survey-open': 'Survey open',
  'resource-open': 'Any resource open',
  'training-profile-open': 'Training profile open',
};

/**
 * Renders legacy and behavior analytics for a Learning Path.
 * @returns {JSX.Element} Statistics page
 */
const LearningPathStatistics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [behaviorStats, setBehaviorStats] = useState(null);
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendRange, setTrendRange] = useState('30d');
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [hoveredTransitionKey, setHoveredTransitionKey] = useState(null);
  const [selectedTransitionKey, setSelectedTransitionKey] = useState(null);
  const [selectedResourceElementId, setSelectedResourceElementId] = useState('all');
  const [heatScopeMode, setHeatScopeMode] = useState('all');
  const [clickDensityViewMode, setClickDensityViewMode] = useState('labels');
  const [selectedFunnelTemplate, setSelectedFunnelTemplate] = useState('learning-intent');
  const [customFunnelStageDraftKeys, setCustomFunnelStageDraftKeys] = useState(['visit', 'hotspot-open', 'resource-open']);
  const [customFunnelStageKeys, setCustomFunnelStageKeys] = useState(['visit', 'hotspot-open', 'resource-open']);
  const [funnelStats, setFunnelStats] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const customFunnelStageKeysQuery = useMemo(() => customFunnelStageKeys.join(','), [customFunnelStageKeys]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [statsResult, behaviorStatsResult, pathResult] = await Promise.all([
        getLearningPathStatistics(id),
        getLearningPathBehaviorStatistics(id),
        getLearningPath(id, { suppressNotification: true }),
      ]);
      setStats(statsResult);
      setBehaviorStats(behaviorStatsResult);
      setPath(pathResult);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const loadFunnelStats = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const result = await getLearningPathFunnelStatistics(
        id,
        selectedFunnelTemplate === 'custom' ? null : selectedFunnelTemplate,
        selectedFunnelTemplate === 'custom' ? customFunnelStageKeysQuery : null,
      );
      setFunnelStats(result);
    } finally {
      setFunnelLoading(false);
    }
  }, [customFunnelStageKeysQuery, id, selectedFunnelTemplate]);

  useEffect(() => {
    loadFunnelStats();
  }, [loadFunnelStats]);

  const copyPublicLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/learning-path/${id}`);
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('learning-path-statistics-qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${path?.name || 'learning-path'}-statistics-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };

    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  const trendDays = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    all: 0,
  };

  const filteredTrend = (stats?.trend || []).filter((point) => {
    const dayCount = trendDays[trendRange];
    if (!dayCount) return true;
    const pointDate = new Date(point.date);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayCount + 1);
    return pointDate >= cutoff;
  });

  const trendChartData = filteredTrend.flatMap((point) => {
    const label = new Date(point.date).toLocaleDateString();
    return [
      { date: label, type: t('learningPathPages.visits', 'Visits'), value: point.visits },
      { date: label, type: t('learningPathPages.clicks', 'Clicks'), value: point.clicks },
    ];
  });

  const legacyColumns = [
    { title: t('learningPathPages.element', 'Element'), dataIndex: 'elementName', key: 'elementName' },
    { title: t('learningPathPages.clicks', 'Clicks'), dataIndex: 'elementClicks', key: 'elementClicks' },
    { title: t('learningPathPages.elementContent.pdf', 'PDF'), dataIndex: 'pdfOpens', key: 'pdfOpens' },
    { title: t('learningPathPages.elementContent.audio', 'Audio'), dataIndex: 'audioOpens', key: 'audioOpens' },
    { title: t('learningPathPages.elementContent.video', 'Video'), dataIndex: 'videoOpens', key: 'videoOpens' },
    { title: t('learningPathPages.links', 'Links'), dataIndex: 'linkOpens', key: 'linkOpens' },
    { title: t('learningPathPages.quizzes', 'Quizzes'), dataIndex: 'quizOpens', key: 'quizOpens' },
    { title: t('learningPathPages.surveys', 'Surveys'), dataIndex: 'surveyOpens', key: 'surveyOpens' },
    { title: t('learningPathPages.trainingProfiles', 'Training Profiles'), dataIndex: 'trainingProfileOpens', key: 'trainingProfileOpens' },
  ];

  const heatOverlayRows = useMemo(
    () => (behaviorStats?.heatOverlay || []).slice().sort((left, right) => right.opens - left.opens || right.uniqueSessions - left.uniqueSessions),
    [behaviorStats?.heatOverlay],
  );

  const elementsById = useMemo(
    () => new Map((path?.elements || []).map((element) => [element.id, element])),
    [path?.elements],
  );

  const heatPointRows = useMemo(() => behaviorStats?.heatPoints || [], [behaviorStats?.heatPoints]);
  const firstClickRows = useMemo(() => behaviorStats?.firstClickElements || [], [behaviorStats?.firstClickElements]);
  const firstClickPointRows = useMemo(() => behaviorStats?.firstClickPoints || [], [behaviorStats?.firstClickPoints]);
  const exitRows = useMemo(() => behaviorStats?.exitElements || [], [behaviorStats?.exitElements]);
  const flowRows = useMemo(() => behaviorStats?.topFlowSequences || [], [behaviorStats?.topFlowSequences]);
  const attentionRows = useMemo(() => behaviorStats?.elementDwell || [], [behaviorStats?.elementDwell]);
  const resourceAttentionRows = useMemo(() => behaviorStats?.resourceDwell || [], [behaviorStats?.resourceDwell]);
  const activeHeatRows = useMemo(
    () => {
      if (heatScopeMode === 'first') {
        return firstClickRows;
      }

      if (heatScopeMode === 'exit') {
        return exitRows;
      }

      return heatOverlayRows;
    },
    [exitRows, firstClickRows, heatOverlayRows, heatScopeMode],
  );
  const activeHeatPointRows = useMemo(
    () => {
      if (heatScopeMode === 'first') {
        return firstClickPointRows;
      }

      if (heatScopeMode === 'exit') {
        return [];
      }

      return heatPointRows;
    },
    [firstClickPointRows, heatPointRows, heatScopeMode],
  );
  const topHeatRows = activeHeatRows.slice(0, 8);

  const getHeatMetricValue = useCallback((row) => row.opens ?? row.count ?? 0, []);

  const maxHeatMetricValue = useMemo(
    () => activeHeatRows.reduce((currentMax, row) => Math.max(currentMax, getHeatMetricValue(row)), 0),
    [activeHeatRows, getHeatMetricValue],
  );

  const getTransitionKey = useCallback(
    (flow) => flow.flowKey,
    [],
  );

  useEffect(() => {
    if (hoveredTransitionKey && !flowRows.some((flow) => getTransitionKey(flow) === hoveredTransitionKey)) {
      setHoveredTransitionKey(null);
    }
    if (selectedTransitionKey && !flowRows.some((flow) => getTransitionKey(flow) === selectedTransitionKey)) {
      setSelectedTransitionKey(null);
    }
  }, [flowRows, getTransitionKey, hoveredTransitionKey, selectedTransitionKey]);

  useEffect(() => {
    if (selectedResourceElementId === 'all') {
      return;
    }

    const hasSelectedElement = resourceAttentionRows.some((row) => String(row.elementId) === String(selectedResourceElementId));
    if (!hasSelectedElement) {
      setSelectedResourceElementId('all');
    }
  }, [resourceAttentionRows, selectedResourceElementId]);

  const activeTransitionKey = hoveredTransitionKey || selectedTransitionKey;

  const activeFlow = useMemo(
    () => flowRows.find((flow) => getTransitionKey(flow) === activeTransitionKey) || null,
    [activeTransitionKey, flowRows, getTransitionKey],
  );

  const activeFlowPreview = useMemo(() => {
    if (!activeFlow) {
      return null;
    }

    const points = (activeFlow.elementIds || [])
      .map((elementId, index) => {
        const element = elementsById.get(elementId);
        if (!element) {
          return null;
        }

        return {
          index,
          element,
          x: Number(element.xPercent) + (Number(element.widthPercent) / 2),
          y: Number(element.yPercent) + (Number(element.heightPercent) / 2),
        };
      })
      .filter(Boolean);

    if (points.length < 2) {
      return null;
    }

    let pathData = `M ${points[0].x} ${points[0].y}`;
    for (let index = 1; index < points.length; index += 1) {
      const previousPoint = points[index - 1];
      const currentPoint = points[index];
      const controlX = (previousPoint.x + currentPoint.x) / 2;
      const controlY = ((previousPoint.y + currentPoint.y) / 2) - Math.max(4, Math.min(12, Math.abs(currentPoint.x - previousPoint.x) * 0.12));
      pathData += ` Q ${controlX} ${controlY} ${currentPoint.x} ${currentPoint.y}`;
    }

    return {
      pathData,
      points,
    };
  }, [activeFlow, elementsById]);

  const resourceElementOptions = useMemo(() => {
    const uniqueElements = new Map();
    resourceAttentionRows.forEach((row) => {
      if (!uniqueElements.has(row.elementId)) {
        uniqueElements.set(row.elementId, row.elementName);
      }
    });

    return [
      {
        value: 'all',
        label: t('learningPathPages.allElements', 'All elements'),
      },
      ...Array.from(uniqueElements.entries())
        .sort((left, right) => left[1].localeCompare(right[1]))
        .map(([elementId, elementName]) => ({
          value: String(elementId),
          label: elementName,
        })),
    ];
  }, [resourceAttentionRows, t]);

  const filteredResourceAttentionRows = useMemo(() => {
    if (selectedResourceElementId === 'all') {
      return resourceAttentionRows;
    }

    return resourceAttentionRows.filter((row) => String(row.elementId) === String(selectedResourceElementId));
  }, [resourceAttentionRows, selectedResourceElementId]);

  const groupedResourceAttentionRows = useMemo(() => {
    const sortedRows = [...filteredResourceAttentionRows]
      .sort((left, right) => left.elementName.localeCompare(right.elementName) || right.opens - left.opens || left.resourceLabel.localeCompare(right.resourceLabel));

    return sortedRows.map((row, index) => {
      const previousRow = sortedRows[index - 1];
      if (previousRow && previousRow.elementId === row.elementId) {
        return {
          ...row,
          elementRowSpan: 0,
        };
      }

      const elementRowSpan = sortedRows.slice(index).filter((candidate) => candidate.elementId === row.elementId).length;
      return {
        ...row,
        elementRowSpan,
      };
    });
  }, [filteredResourceAttentionRows]);

  const clickDensityViewOptions = useMemo(() => ([
    { label: t('learningPathPages.heatViewLabels', 'Labels'), value: 'labels' },
    { label: t('learningPathPages.heatViewIntensity', 'Heat colors'), value: 'intensity' },
  ]), [t]);

  const heatScopeOptions = useMemo(() => ([
    { label: t('learningPathPages.heatScopeAll', 'All clicks'), value: 'all' },
    { label: t('learningPathPages.heatScopeFirst', 'First click'), value: 'first' },
    { label: t('learningPathPages.heatScopeExit', 'Exit click'), value: 'exit' },
  ]), [t]);

  const normalizeResourceTypeKey = useCallback((value) => {
    const resourceTypeKeyByNumber = {
      0: 'pdf',
      1: 'audio',
      2: 'video',
      3: 'link',
      4: 'quiz',
      5: 'survey',
      6: 'trainingProfile',
      7: 'info',
    };

    if (typeof value === 'number') {
      return resourceTypeKeyByNumber[value] || String(value);
    }

    const normalizedValue = String(value || '')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase();

    const slugToTranslationKey = {
      'training-profile': 'trainingProfile',
      'rich-content': 'info',
    };

    return slugToTranslationKey[normalizedValue] || normalizedValue;
  }, []);

  const getHeatIntensityStyle = useCallback((row, element) => {
    const ratio = maxHeatMetricValue > 0 ? getHeatMetricValue(row) / maxHeatMetricValue : 0;
    const hue = 50 - (50 * ratio);
    const lightness = 86 - (36 * ratio);

    return {
      left: `${element.xPercent}%`,
      top: `${element.yPercent}%`,
      width: `${element.widthPercent}%`,
      height: `${element.heightPercent}%`,
      background: `hsl(${hue}, 100%, ${lightness}%)`,
      boxShadow: ratio > 0 ? `0 0 ${12 + (ratio * 20)}px rgba(15, 23, 42, 0.12)` : 'none',
    };
  }, [getHeatMetricValue, maxHeatMetricValue]);

  const exitColumns = useMemo(() => ([
    { title: t('learningPathPages.element', 'Element'), dataIndex: 'elementName', key: 'elementName' },
    { title: t('learningPathPages.exits', 'Exits'), dataIndex: 'count', key: 'count' },
    { title: t('learningPathPages.explicitExits', 'Explicit'), dataIndex: 'explicitCount', key: 'explicitCount' },
    { title: t('learningPathPages.inferredExits', 'Inferred'), dataIndex: 'inferredCount', key: 'inferredCount' },
    {
      title: t('learningPathPages.share', 'Share'),
      dataIndex: 'shareOfSessions',
      key: 'shareOfSessions',
      render: (value) => `${Number(value || 0).toFixed(1)}%`,
    },
  ]), [t]);

  const heatColumns = useMemo(() => {
    if (heatScopeMode === 'first') {
      return [
        { title: t('learningPathPages.element', 'Element'), dataIndex: 'elementName', key: 'elementName' },
        { title: t('learningPathPages.firstClicks', 'First clicks'), dataIndex: 'count', key: 'count' },
        {
          title: t('learningPathPages.share', 'Share'),
          dataIndex: 'shareOfSessions',
          key: 'shareOfSessions',
          render: (value) => `${Number(value || 0).toFixed(1)}%`,
        },
      ];
    }

    if (heatScopeMode === 'exit') {
      return exitColumns;
    }

    return [
      { title: t('learningPathPages.element', 'Element'), dataIndex: 'elementName', key: 'elementName' },
      { title: t('learningPathPages.opens', 'Opens'), dataIndex: 'opens', key: 'opens' },
      { title: t('learningPathPages.visits', 'Visits'), dataIndex: 'uniqueSessions', key: 'uniqueSessions' },
      {
        title: t('learningPathPages.repeatOpens', 'Repeat opens'),
        key: 'repeatOpens',
        render: (_, row) => Math.max(0, (row.opens || 0) - (row.uniqueSessions || 0)),
      },
    ];
  }, [exitColumns, heatScopeMode, t]);

  const flowColumns = [
    {
      title: t('learningPathPages.flowSequence', 'Sequence'),
      key: 'sequence',
      render: (_, row) => (
        <div className={styles.statsFlowSequence} title={(row.elementNames || []).join(' -> ')}>
          {(row.elementNames || []).join(' -> ')}
        </div>
      ),
    },
    { title: t('learningPathPages.visits', 'Visits'), dataIndex: 'count', key: 'count' },
    {
      title: t('learningPathPages.share', 'Share'),
      dataIndex: 'shareOfFlows',
      key: 'shareOfFlows',
      render: (value) => `${Number(value || 0).toFixed(1)}%`,
    },
  ];

  const attentionColumns = [
    { title: t('learningPathPages.element', 'Element'), dataIndex: 'elementName', key: 'elementName' },
    { title: t('learningPathPages.opens', 'Opens'), dataIndex: 'opens', key: 'opens' },
    {
      title: t('learningPathPages.averageDwell', 'Average dwell'),
      dataIndex: 'averageDwellMs',
      key: 'averageDwellMs',
      render: (value) => formatDurationFromMilliseconds(value),
    },
    {
      title: t('learningPathPages.medianDwell', 'Median dwell'),
      dataIndex: 'medianDwellMs',
      key: 'medianDwellMs',
      render: (value) => formatDurationFromMilliseconds(value),
    },
    {
      title: (
        <Space size="small">
          <span>{t('learningPathPages.dropOff', 'Drop-off')}</span>
          <Tooltip title={t('learningPathPages.dropOffHelp', 'Visits that ended on this element without moving to another element afterward.')}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: 'dropOff',
      render: (_, row) => (
        <div className={styles.statsDropOffCell}>
          <span>{row.dropOffCount}</span>
          <Progress
            percent={Math.max(0, Math.min(100, Math.round(row.dropOffRate || 0)))}
            size="small"
            showInfo={false}
            strokeColor="#f97316"
          />
          <span>{Number(row.dropOffRate || 0).toFixed(1)}%</span>
        </div>
      ),
    },
  ];

  const resourceColumns = [
    {
      title: t('learningPathPages.element', 'Element'),
      dataIndex: 'elementName',
      key: 'elementName',
      render: (_, row) => ({
        children: row.elementName,
        props: {
          rowSpan: row.elementRowSpan,
        },
      }),
    },
    {
      title: t('learningPathPages.resource', 'Resource'),
      dataIndex: 'resourceLabel',
      key: 'resourceLabel',
      render: (value) => <span className={styles.statsResourceLabel}>{value}</span>,
    },
    {
      title: t('learningPathPages.resourceType', 'Resource type'),
      dataIndex: 'resourceType',
      key: 'resourceType',
      render: (value) => t(`learningPathPages.elementContent.${normalizeResourceTypeKey(value)}`, String(value)),
    },
    { title: t('learningPathPages.visits', 'Visits'), dataIndex: 'uniqueVisits', key: 'uniqueVisits' },
    { title: t('learningPathPages.opens', 'Opens'), dataIndex: 'opens', key: 'opens' },
    {
      title: t('learningPathPages.averageDwell', 'Average dwell'),
      dataIndex: 'averageDwellMs',
      key: 'averageDwellMs',
      render: (value) => formatDurationFromMilliseconds(value),
    },
    {
      title: t('learningPathPages.medianDwell', 'Median dwell'),
      dataIndex: 'medianDwellMs',
      key: 'medianDwellMs',
      render: (value) => formatDurationFromMilliseconds(value),
    },
  ];

  const getFunnelTemplateLabel = useCallback(
    (template) => t(template.labelKey, funnelTemplateFallbackLabels[template.key] || template.key),
    [t],
  );

  const getFunnelTemplateDescription = useCallback(
    (template) => t(template.descriptionKey, funnelTemplateFallbackDescriptions[template.key] || template.key),
    [t],
  );

  const getFunnelStageLabel = useCallback(
    (stage) => t(stage.labelKey, funnelStageFallbackLabels[stage.stageKey] || stage.stageKey),
    [t],
  );

  const getFunnelStageLabelByKey = useCallback(
    (stageKey) => {
      const labelKeySuffix = stageKey.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      return t(`learningPathPages.funnelStage.${labelKeySuffix}`, funnelStageFallbackLabels[stageKey] || stageKey);
    },
    [t],
  );

  const funnelStageOptions = useMemo(
    () => Object.keys(funnelStageFallbackLabels)
      .filter((stageKey) => stageKey !== 'visit')
      .map((stageKey) => ({
      value: stageKey,
      label: getFunnelStageLabelByKey(stageKey),
      })),
    [getFunnelStageLabelByKey],
  );

  const updateCustomFunnelStage = useCallback((index, nextStageKey) => {
    setCustomFunnelStageDraftKeys((currentStageKeys) => currentStageKeys.map((stageKey, stageIndex) => (stageIndex === index ? nextStageKey : stageKey)));
  }, []);

  const addCustomFunnelStage = useCallback(() => {
    setCustomFunnelStageDraftKeys((currentStageKeys) => {
      const nextStage = funnelStageOptions[0]?.value || 'resource-open';
      return [...currentStageKeys, nextStage];
    });
  }, [funnelStageOptions]);

  const moveCustomFunnelStage = useCallback((index, direction) => {
    setCustomFunnelStageDraftKeys((currentStageKeys) => {
      if (index === 0) {
        return currentStageKeys;
      }

      const nextIndex = index + direction;
      if (nextIndex <= 0 || nextIndex >= currentStageKeys.length) {
        return currentStageKeys;
      }

      const nextStageKeys = [...currentStageKeys];
      const [movedStage] = nextStageKeys.splice(index, 1);
      nextStageKeys.splice(nextIndex, 0, movedStage);
      return nextStageKeys;
    });
  }, []);

  const removeCustomFunnelStage = useCallback((index) => {
    setCustomFunnelStageDraftKeys((currentStageKeys) => {
      if (index === 0 || currentStageKeys.length <= 2) {
        return currentStageKeys;
      }

      return currentStageKeys.filter((_, stageIndex) => stageIndex !== index);
    });
  }, []);

  const applyCustomFunnelStageDraft = useCallback(() => {
    setCustomFunnelStageKeys(customFunnelStageDraftKeys);
  }, [customFunnelStageDraftKeys]);

  const resetCustomFunnelStageDraft = useCallback(() => {
    setCustomFunnelStageDraftKeys(customFunnelStageKeys);
  }, [customFunnelStageKeys]);

  const hasPendingCustomFunnelChanges = useMemo(
    () => customFunnelStageDraftKeys.join('|') !== customFunnelStageKeys.join('|'),
    [customFunnelStageDraftKeys, customFunnelStageKeys],
  );

  const funnelTemplateOptions = useMemo(() => {
    const templates = funnelStats?.availableTemplates?.length
      ? funnelStats.availableTemplates
      : Object.entries(funnelTemplateFallbackLabels).map(([key]) => ({
        key,
        labelKey: `learningPathPages.funnelTemplate.${key}`,
        descriptionKey: `learningPathPages.funnelTemplateDescription.${key}`,
        stageKeys: [],
      }));

    return templates.map((template) => ({
      ...template,
      value: template.key,
      label: getFunnelTemplateLabel(template),
      description: getFunnelTemplateDescription(template),
    }));
  }, [funnelStats?.availableTemplates, getFunnelTemplateDescription, getFunnelTemplateLabel]);

  const customFunnelTemplateOption = useMemo(() => ({
    key: 'custom',
    value: 'custom',
    labelKey: 'learningPathPages.funnelTemplate.custom',
    descriptionKey: 'learningPathPages.funnelTemplateDescription.custom',
    stageKeys: customFunnelStageDraftKeys,
    label: t('learningPathPages.funnelTemplate.custom', 'Custom funnel'),
    description: t('learningPathPages.funnelTemplateDescription.custom', 'Build your own ordered sequence of steps for this learning path.'),
  }), [customFunnelStageDraftKeys, t]);

  const allFunnelTemplateOptions = useMemo(
    () => [...funnelTemplateOptions, customFunnelTemplateOption],
    [customFunnelTemplateOption, funnelTemplateOptions],
  );

  const selectedFunnelTemplateOption = useMemo(
    () => allFunnelTemplateOptions.find((template) => template.value === selectedFunnelTemplate) || null,
    [allFunnelTemplateOptions, selectedFunnelTemplate],
  );

  const funnelColumns = useMemo(() => ([
    {
      title: t('learningPathPages.funnelStageTitle', 'Stage'),
      dataIndex: 'stageKey',
      key: 'stageKey',
      render: (_, row) => getFunnelStageLabel(row),
    },
    { title: t('learningPathPages.visits', 'Visits'), dataIndex: 'count', key: 'count' },
    {
      title: t('learningPathPages.shareOfVisits', 'Share of visits'),
      dataIndex: 'shareOfSessions',
      key: 'shareOfSessions',
      render: (value) => `${Number(value || 0).toFixed(1)}%`,
    },
    {
      title: t('learningPathPages.stepConversion', 'From previous step'),
      dataIndex: 'stepConversionRate',
      key: 'stepConversionRate',
      render: (value) => `${Number(value || 0).toFixed(1)}%`,
    },
  ]), [getFunnelStageLabel, t]);

  return (
    <PagesWrapper>
      <div className={styles.editorContainer}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/learning-paths/${id}`)}>
              {t('learningPathPages.back', 'Back')}
            </Button>
            <h1>{stats?.learningPathName || t('learningPathPages.statisticsTitle', 'Learning Path Statistics')}</h1>
            {path && <Tag color={path.isPublished ? 'green' : 'default'}>{path.isPublished ? t('learningPathPages.published', 'Published') : t('learningPathPages.draft', 'Draft')}</Tag>}
            {path?.countryLanguage && <Tag>{path.countryLanguage}</Tag>}
            {path?.addedByName && (
              <Tooltip title={t('learningPathPages.createdBy', 'Created by')}>
                <Space size="small" className={styles.createdBy}>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>{path.addedByName}</span>
                </Space>
              </Tooltip>
            )}
          </div>
          <Space className={styles.headerActions}>
            <Tooltip title={t('learningPathPages.openMap', 'Open map')}>
              <Button icon={<EyeOutlined />} disabled={!path?.isPublished} onClick={() => window.open(`/learning-path/${id}`, '_blank')}>
                {t('learningPathPages.openMap', 'Open map')}
              </Button>
            </Tooltip>
            <Tooltip title={t('learningPathPages.copyLink', 'Copy Link')}>
              <Button icon={<CopyOutlined />} disabled={!path?.isPublished} onClick={copyPublicLink}>
                {t('learningPathPages.copyLink', 'Copy Link')}
              </Button>
            </Tooltip>
            <Tooltip title={t('learningPathPages.qrCode', 'QR Code')}>
              <Button icon={<QrcodeOutlined />} disabled={!path?.isPublished} onClick={() => setQrModalVisible(true)}>
                {t('learningPathPages.qrCode', 'QR Code')}
              </Button>
            </Tooltip>
            <Button icon={<ReloadOutlined />} onClick={() => { loadStats(); loadFunnelStats(); }}>{t('learningPathPages.refresh', 'Refresh')}</Button>
          </Space>
        </div>

        {loading ? <Spin /> : (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} md={8}><Card className={styles.statsSummaryCard}><Statistic title={t('learningPathPages.totalVisits', 'Total Visits')} value={stats?.totalVisits || 0} /></Card></Col>
              <Col xs={24} md={8}><Card className={styles.statsSummaryCard}><Statistic title={t('learningPathPages.uniqueVisitors', 'Unique Visitors')} value={stats?.uniqueVisitors || 0} /></Card></Col>
              <Col xs={24} md={8}><Card className={styles.statsSummaryCard}><Statistic title={t('learningPathPages.totalElementClicks', 'Element Clicks')} value={stats?.totalElementClicks || 0} /></Card></Col>
              <Col xs={24} md={8}><Card className={styles.statsSummaryCard}><Statistic title={t('learningPathPages.trackedVisits', 'Tracked visits')} value={behaviorStats?.totalSessions || 0} /></Card></Col>
              <Col xs={24} md={8}><Card className={styles.statsSummaryCard}><Statistic title={t('learningPathPages.averageVisitDuration', 'Average visit duration')} value={formatDurationFromMilliseconds(behaviorStats?.averageSessionDurationMs || 0)} /></Card></Col>
              <Col xs={24} md={8}><Card className={styles.statsSummaryCard}><Statistic title={t('learningPathPages.averageResourcesOpenedPerVisit', 'Average resources opened / visit')} value={Number(behaviorStats?.averageResourcesOpened || 0).toFixed(1)} /></Card></Col>
            </Row>

            <Card className={styles.statsSectionCard} title={stats?.learningPathName || t('learningPathPages.title', 'Learning Path')}>
              <Table rowKey="elementId" dataSource={stats?.elementStatistics || []} columns={legacyColumns} pagination={false} />
            </Card>

            <Card className={styles.statsSectionCard} title={t('learningPathPages.dailyTrend', 'Daily trend')} style={{ marginTop: 16 }} extra={(
              <Space>
                {[
                  { key: '7d', label: t('learningPathPages.range7d', '7D') },
                  { key: '30d', label: t('learningPathPages.range30d', '30D') },
                  { key: '90d', label: t('learningPathPages.range90d', '90D') },
                  { key: 'all', label: t('learningPathPages.rangeAll', 'All') },
                ].map((option) => (
                  <Button key={option.key} type={trendRange === option.key ? 'primary' : 'default'} size="small" onClick={() => setTrendRange(option.key)}>
                    {option.label}
                  </Button>
                ))}
              </Space>
            )}>
              {trendChartData.length ? (
                <div className={styles.chartContainer}>
                  <Line
                    data={trendChartData}
                    xField="date"
                    yField="value"
                    seriesField="type"
                    point={{ size: 3 }}
                    smooth
                    color={["#2563eb", "#14b8a6"]}
                    yAxis={{ title: { text: t('learningPathPages.interactions', 'Interactions') } }}
                    xAxis={{ title: { text: t('learningPathPages.date', 'Date') } }}
                  />
                </div>
              ) : (
                <Empty description={t('learningPathPages.noTrendData', 'No trend data available')} />
              )}
            </Card>

            <div className={styles.statsSectionHeader}>
              <div>
                <h3>{t('learningPathPages.heatSectionTitle', 'Heat')}</h3>
                <p>{t('learningPathPages.heatSectionDescription', 'Spatial click density shows where learners actually interact on the map, while the hotspot table keeps the element-level intensity view.')}</p>
              </div>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} xl={15}>
                <Card
                  className={styles.statsSectionCard}
                  title={heatScopeMode === 'first'
                    ? t('learningPathPages.firstClickMap', 'First-click map')
                    : heatScopeMode === 'exit'
                      ? t('learningPathPages.exitMap', 'Exit map')
                      : t('learningPathPages.clickDensityMap', 'Click density')}
                  extra={(
                    <Space size="small" wrap>
                      <Segmented
                        size="small"
                        value={heatScopeMode}
                        options={heatScopeOptions}
                        onChange={setHeatScopeMode}
                      />
                      <Segmented
                        size="small"
                        value={clickDensityViewMode}
                        options={clickDensityViewOptions}
                        onChange={setClickDensityViewMode}
                      />
                      {heatScopeMode === 'exit' ? (
                        <Space size="small">
                          <FireOutlined />
                          <span>{behaviorStats?.totalTrackedExits || 0} {t('learningPathPages.exits', 'Exits')}</span>
                        </Space>
                      ) : (
                        <Space size="small">
                          <FireOutlined />
                          <span>{activeHeatPointRows.length} {t('learningPathPages.points', 'points')}</span>
                        </Space>
                      )}
                    </Space>
                  )}
                >
                  {path?.backgroundImage ? (
                    <div className={styles.statsHeatCanvasShell}>
                      <div className={`${styles.a4Page} ${styles.statsHeatPage} ${clickDensityViewMode === 'intensity' ? styles.statsHeatPageBlurred : ''}`}>
                        <img
                          src={getFileUrl(path.backgroundImage.relativeUrl) || `/api/files/${path.backgroundImage.id}/download`}
                          alt={path.backgroundImage.altText || path.name}
                          className={styles.backgroundImage}
                        />
                        <div className={styles.statsHeatLayer}>
                          {clickDensityViewMode === 'labels' && activeHeatPointRows.map((point, index) => (
                            <span
                              key={`${point.elementId}-${index}-${point.xPercent}-${point.yPercent}`}
                              className={styles.statsHeatPoint}
                              style={{ left: `${point.xPercent}%`, top: `${point.yPercent}%` }}
                              title={`${point.elementName} (${Number(point.xPercent).toFixed(1)}%, ${Number(point.yPercent).toFixed(1)}%)`}
                            />
                          ))}
                          {clickDensityViewMode === 'labels' && topHeatRows.map((row) => {
                            const element = elementsById.get(row.elementId);
                            if (!element) {
                              return null;
                            }

                            return (
                              <div
                                key={row.elementId}
                                className={styles.statsHeatLabel}
                                style={{
                                  left: `${element.xPercent + (element.widthPercent / 2)}%`,
                                  top: `${element.yPercent + (element.heightPercent / 2)}%`,
                                }}
                              >
                                <span className={styles.statsHeatLabelCount}>{getHeatMetricValue(row)}</span>
                                <span className={styles.statsHeatLabelName}>{row.elementName}</span>
                              </div>
                            );
                          })}
                          {clickDensityViewMode === 'intensity' && activeHeatRows.map((row) => {
                            const element = elementsById.get(row.elementId);
                            if (!element || !(getHeatMetricValue(row) > 0)) {
                              return null;
                            }

                            return (
                              <div
                                key={`intensity-${row.elementId}`}
                                className={styles.statsHeatElementIntensity}
                                style={getHeatIntensityStyle(row, element)}
                                title={`${row.elementName}: ${getHeatMetricValue(row)} ${heatScopeMode === 'first' ? t('learningPathPages.firstClicks', 'First clicks') : heatScopeMode === 'exit' ? t('learningPathPages.exits', 'Exits') : t('learningPathPages.opens', 'Opens')}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Empty description={t('learningPathPages.play.noBackground', 'No background image available.')} />
                  )}
                </Card>
              </Col>
              <Col xs={24} xl={9}>
                <Card className={styles.statsSectionCard} title={heatScopeMode === 'first' ? t('learningPathPages.firstClickElements', 'First-click elements') : heatScopeMode === 'exit' ? t('learningPathPages.exitElements', 'Exit elements') : t('learningPathPages.hotElements', 'Hot elements')}>
                  <Table rowKey="elementId" dataSource={activeHeatRows} columns={heatColumns} pagination={false} size="small" />
                </Card>
              </Col>
            </Row>

            <div className={styles.statsSectionHeader}>
              <div>
                <h3>{t('learningPathPages.flowSectionTitle', 'Flow')}</h3>
                <p>{t('learningPathPages.flowSectionDescription', 'Sequence flow reconstructs the most common next step learners take after opening an element.')}</p>
              </div>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} xl={15}>
                <Card className={styles.statsSectionCard} title={t('learningPathPages.flowPreview', 'Flow preview')} extra={activeFlow ? <Tag color="blue">{activeFlow.count} {t('learningPathPages.visits', 'Visits')}</Tag> : <LineChartOutlined />}>
                  {path?.backgroundImage ? (
                    <>
                      <div className={styles.statsFlowCanvasShell}>
                        <div className={`${styles.a4Page} ${styles.statsFlowPage}`}>
                          <img
                            src={getFileUrl(path.backgroundImage.relativeUrl) || `/api/files/${path.backgroundImage.id}/download`}
                            alt={path.backgroundImage.altText || path.name}
                            className={styles.backgroundImage}
                          />
                          <div className={styles.statsFlowLayer}>
                            <svg className={styles.statsFlowSvg} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                              <defs>
                                <marker id="statsFlowArrowHead" markerWidth="5" markerHeight="5" refX="4.5" refY="2.5" orient="auto">
                                  <path d="M 0 0 L 5 2.5 L 0 5 z" className={styles.statsFlowArrowHead} />
                                </marker>
                              </defs>
                              {activeFlowPreview && (
                                <path
                                  d={activeFlowPreview.pathData}
                                  className={styles.statsFlowPath}
                                  markerEnd="url(#statsFlowArrowHead)"
                                />
                              )}
                            </svg>

                            {(path?.elements || []).map((element) => {
                              const centerLeft = Number(element.xPercent) + (Number(element.widthPercent) / 2);
                              const centerTop = Number(element.yPercent) + (Number(element.heightPercent) / 2);
                              const activePoint = activeFlowPreview?.points.find((point) => point.element.id === element.id);

                              return (
                                <React.Fragment key={`flow-node-${element.id}`}>
                                  <span
                                    className={`${styles.statsFlowAnchor} ${activePoint ? styles.statsFlowAnchorActive : ''}`}
                                    style={{ left: `${centerLeft}%`, top: `${centerTop}%` }}
                                    title={element.name}
                                  />
                                  {activePoint && (
                                    <div
                                      className={styles.statsFlowNodeLabel}
                                      style={{ left: `${centerLeft}%`, top: `${centerTop}%` }}
                                    >
                                      <span className={styles.statsFlowNodeOrder}>{activePoint.index + 1}</span>
                                      {element.name}
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      {activeFlow ? (
                        <div className={styles.statsFlowMeta}>
                          <span className={styles.statsFlowMetaSequence}>{`${(activeFlow.elementNames || []).join(' -> ')} | ${activeFlow.count} ${t('learningPathPages.visits', 'Visits')} | ${Number(activeFlow.shareOfFlows || 0).toFixed(1)}% ${t('learningPathPages.share', 'Share')}`}</span>
                        </div>
                      ) : (
                        <div className={styles.statsFlowHint}>
                          {t('learningPathPages.hoverTransitionHint', 'Hover or click a flow in the table to preview and pin its path on the map.')}
                        </div>
                      )}
                    </>
                  ) : (
                    <Empty description={t('learningPathPages.play.noBackground', 'No background image available.')} />
                  )}
                </Card>
              </Col>
              <Col xs={24} xl={9}>
                <Card className={styles.statsSectionCard} title={t('learningPathPages.commonFlows', 'Common flows')}>
                  {flowRows.length ? (
                    <Table
                      rowKey={getTransitionKey}
                      dataSource={flowRows}
                      columns={flowColumns}
                      pagination={false}
                      size="small"
                      onRow={(record) => ({
                        className: getTransitionKey(record) === activeTransitionKey ? styles.statsFlowRowActive : '',
                        onMouseEnter: () => {
                          const key = getTransitionKey(record);
                          setHoveredTransitionKey(key);
                          setSelectedTransitionKey(key);
                        },
                        onMouseLeave: () => setHoveredTransitionKey((currentKey) => (currentKey === getTransitionKey(record) ? null : currentKey)),
                        onClick: () => setSelectedTransitionKey(getTransitionKey(record)),
                      })}
                    />
                  ) : (
                    <Empty description={t('learningPathPages.noFlowData', 'No sequence flow data available yet.')} />
                  )}
                </Card>
              </Col>
            </Row>

            <div className={styles.statsSectionHeader}>
              <div>
                <h3>{t('learningPathPages.attentionSectionTitle', 'Attention')}</h3>
                <p>{t('learningPathPages.attentionSectionDescription', 'Attention metrics combine element dwell, resource dwell, and drop-off patterns so you can see where learners stay and where they leave.')}</p>
              </div>
            </div>

            <Tabs
              className={styles.statsTabs}
              items={[
                {
                  key: 'elements',
                  label: t('learningPathPages.elementAttention', 'Element attention'),
                  children: (
                    <Card className={styles.statsSectionCard} bordered={false}>
                      {attentionRows.length ? (
                        <Table rowKey="elementId" dataSource={attentionRows} columns={attentionColumns} pagination={false} size="small" />
                      ) : (
                        <Empty description={t('learningPathPages.noAttentionData', 'No dwell data available yet.')} />
                      )}
                    </Card>
                  ),
                },
                {
                  key: 'resources',
                  label: t('learningPathPages.resourceAttention', 'Resource attention'),
                  children: (
                    <Card
                      className={styles.statsSectionCard}
                      bordered={false}
                      extra={(
                        <Space size="small" wrap>
                          <span className={styles.statsFilterLabel}>{t('learningPathPages.resourceFilterLabel', 'Element')}</span>
                          <Select
                            size="small"
                            value={selectedResourceElementId}
                            options={resourceElementOptions}
                            onChange={setSelectedResourceElementId}
                            className={styles.statsResourceFilter}
                          />
                        </Space>
                      )}
                    >
                      {groupedResourceAttentionRows.length ? (
                        <Table rowKey="resourceKey" dataSource={groupedResourceAttentionRows} columns={resourceColumns} pagination={false} size="small" />
                      ) : (
                        <Empty description={t('learningPathPages.noResourceAttentionData', 'No resource dwell data available yet.')} />
                      )}
                    </Card>
                  ),
                },
                {
                  key: 'funnels',
                  label: t('learningPathPages.funnels', 'Funnels'),
                  children: (
                    <Card
                      className={`${styles.statsSectionCard} ${styles.statsFunnelTab}`}
                      bordered={false}
                      title={t('learningPathPages.funnels', 'Funnels')}
                    >
                      <div className={styles.statsFunnelIntro}>
                        <span>{t('learningPathPages.funnelsMeaningInline', 'Funnel = visits that reach each step in this order.')}</span>
                      </div>

                      <div className={styles.statsFunnelTemplateGrid}>
                        {allFunnelTemplateOptions.map((template) => (
                          <button
                            key={template.value}
                            type="button"
                            className={`${styles.statsFunnelTemplateCard} ${template.value === selectedFunnelTemplate ? styles.statsFunnelTemplateCardActive : ''}`}
                            onClick={() => setSelectedFunnelTemplate(template.value)}
                          >
                            <strong>{template.label}</strong>
                            <span>{template.description}</span>
                            {!!template.stageKeys?.length && (
                              <div className={styles.statsFunnelStagePreview}>
                                {template.stageKeys.map((stageKey) => (
                                  <span key={`${template.value}-${stageKey}`} className={styles.statsFunnelStageChip}>
                                    {getFunnelStageLabelByKey(stageKey)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>

                      {selectedFunnelTemplate === 'custom' && (
                        <div className={styles.statsFunnelBuilder}>
                          <div className={styles.statsFunnelBuilderHeader}>
                            <strong>{t('learningPathPages.customSequence', 'Custom sequence')}</strong>
                            <Button size="small" icon={<PlusOutlined />} onClick={addCustomFunnelStage}>
                              {t('learningPathPages.addStep', 'Add step')}
                            </Button>
                          </div>
                          <div className={styles.statsFunnelBuilderRows}>
                            {customFunnelStageDraftKeys.map((stageKey, index) => (
                              <div key={`custom-stage-${index}-${stageKey}`} className={styles.statsFunnelBuilderRow}>
                                <span className={styles.statsFunnelBuilderIndex}>{index + 1}</span>
                                {index === 0 ? (
                                  <div className={styles.statsFunnelFixedStage}>{getFunnelStageLabelByKey(stageKey)}</div>
                                ) : (
                                  <Select
                                    size="small"
                                    value={stageKey}
                                    options={funnelStageOptions}
                                    onChange={(nextValue) => updateCustomFunnelStage(index, nextValue)}
                                    className={styles.statsFunnelStageSelect}
                                  />
                                )}
                                <Space size="small" className={styles.statsFunnelBuilderActions}>
                                  <Button
                                    size="small"
                                    icon={<ArrowUpOutlined />}
                                    onClick={() => moveCustomFunnelStage(index, -1)}
                                    disabled={index <= 1}
                                    aria-label={t('learningPathPages.moveUp', 'Move up')}
                                  />
                                  <Button
                                    size="small"
                                    icon={<ArrowDownOutlined />}
                                    onClick={() => moveCustomFunnelStage(index, 1)}
                                    disabled={index === 0 || index === customFunnelStageDraftKeys.length - 1}
                                    aria-label={t('learningPathPages.moveDown', 'Move down')}
                                  />
                                  <Button
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeCustomFunnelStage(index)}
                                    disabled={index === 0 || customFunnelStageDraftKeys.length <= 2}
                                    aria-label={t('learningPathPages.removeStep', 'Remove step')}
                                  />
                                </Space>
                              </div>
                            ))}
                          </div>
                          <Space size="small" className={styles.statsFunnelBuilderFooter}>
                            <Button type="primary" size="small" onClick={applyCustomFunnelStageDraft} disabled={!hasPendingCustomFunnelChanges}>
                              {t('learningPathPages.applySequence', 'Apply sequence')}
                            </Button>
                            <Button size="small" onClick={resetCustomFunnelStageDraft} disabled={!hasPendingCustomFunnelChanges}>
                              {t('learningPathPages.resetSequence', 'Reset changes')}
                            </Button>
                          </Space>
                        </div>
                      )}

                      {selectedFunnelTemplateOption && (
                        <div className={styles.statsFunnelSelectedTemplate}>
                          <strong>{selectedFunnelTemplateOption.label}</strong>
                          <span>{selectedFunnelTemplateOption.description}</span>
                        </div>
                      )}

                      {funnelLoading ? (
                        <Spin />
                      ) : funnelStats?.stages?.length ? (
                        <>
                          <div className={styles.statsFunnelStageCards}>
                            {funnelStats.stages.map((stage) => (
                              <div key={`funnel-card-${stage.stageKey}`} className={styles.statsFunnelStageCard}>
                                <strong>{getFunnelStageLabel(stage)}</strong>
                                <span className={styles.statsFunnelStageCount}>{stage.count}</span>
                                <span>{`${Number(stage.shareOfSessions || 0).toFixed(1)}% ${t('learningPathPages.shareOfVisits', 'Share of visits')}`}</span>
                                <span>{`${Number(stage.stepConversionRate || 0).toFixed(1)}% ${t('learningPathPages.stepConversion', 'From previous step')}`}</span>
                              </div>
                            ))}
                          </div>
                          <Table rowKey="stageKey" dataSource={funnelStats.stages} columns={funnelColumns} pagination={false} size="small" />
                        </>
                      ) : (
                        <Empty description={t('learningPathPages.noFunnelData', 'No funnel data available yet.')} />
                      )}
                    </Card>
                  ),
                },
              ]}
            />
          </>
        )}

        {path && (
          <Modal
            title={t('learningPathPages.qrCode', 'QR Code')}
            open={qrModalVisible}
            onCancel={() => setQrModalVisible(false)}
            centered
            footer={[
              <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadQrCode}>
                {t('learningPathPages.downloadQrCode', 'Download QR Code')}
              </Button>,
              <Button key="close" onClick={() => setQrModalVisible(false)}>
                {t('learningPathPages.close', 'Close')}
              </Button>,
            ]}
          >
            <div className={styles.qrCodeContainer}>
              <QRCodeSVG
                id="learning-path-statistics-qr-code"
                value={`${window.location.origin}/learning-path/${path.id}`}
                size={256}
                level="H"
                includeMargin
              />
              <p className={styles.qrCodeCollectionName}>{path.name}</p>
              <p className={styles.qrCodeLink}>{`${window.location.origin}/learning-path/${path.id}`}</p>
            </div>
          </Modal>
        )}
      </div>
    </PagesWrapper>
  );
};

export default LearningPathStatistics;
